import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSellerListings, SellerListing, ItemPromotion } from '../../hooks/useSellerListings';
import { useMarketplaceConnections } from '../../hooks/useMarketplaceConnections';
import { useSellerAnalysis, useAnalysisPrefs, type HealthCard } from '../../hooks/useSellerAnalysis';
import { CreditConfirmRunButton } from '../credits/CreditConfirmRunButton';
import { api } from '../../services/api';
import { SellerAutomationHeader, SellerRunButton, sellerRunButtonClass } from './SellerAutomationHeader';
import { SegmentedToggle, EmptyState, BoardGroupingToggle, useBoardGrouping, type OverflowItem } from './sellerUi';
import { HealthBadge, ListingDiagnosisDrawer, ListingsBoard, listingFlowStage } from './ListingHealth';
import { SellerBoostDrawer, useShopeeBoost } from './SellerBoostView';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function formatDateTime(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function MarketplaceBadge({ marketplace }: { marketplace: string }) {
  if (marketplace === 'mercadolivre') {
    return <span className="inline-flex items-center gap-1 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">ML</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700">Shopee</span>;
}

function SortButton({ label, field, current, order, onSort }: { label: string; field: string; current: string; order: string; onSort: (f: string) => void }) {
  const isActive = current === field;
  return (
    <button
      onClick={() => onSort(field)}
      className={`text-xs font-medium whitespace-nowrap ${isActive ? 'text-orange-700' : 'text-gray-500 hover:text-gray-700'}`}
    >
      {label}
      {isActive && <span className="ml-0.5">{order === 'desc' ? '↓' : '↑'}</span>}
    </button>
  );
}

function DeltaBadge({ delta }: { delta: number | undefined | null }) {
  if (delta == null || delta === 0) return null;
  const isPositive = delta > 0;
  return (
    <span className={`ml-1 inline-flex items-center text-[9px] font-semibold ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
      {isPositive ? '↑' : '↓'}{Math.abs(delta).toFixed(0)}%
    </span>
  );
}

function PromoBadge({ promotions, onClick }: { promotions: ItemPromotion[]; onClick: () => void }) {
  const active = promotions.filter(p => p.status === 'ongoing' || p.status === 'upcoming');
  if (active.length === 0) return null;
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-0.5 whitespace-nowrap rounded-full bg-purple-50 border border-purple-200 px-1.5 py-0.5 text-[9px] font-semibold text-purple-700 hover:bg-purple-100 transition"
    >
      {active.length} promo{active.length > 1 ? 's' : ''}
    </button>
  );
}

function PriceDisplay({ originalPrice, currentPrice, discountPercent }: { originalPrice?: number | null; currentPrice?: number | null; discountPercent?: number | null }) {
  if (!currentPrice && !originalPrice) return <span className="text-xs text-gray-300">-</span>;
  const hasDiscount = originalPrice && currentPrice && originalPrice > currentPrice;
  return (
    <div className="flex flex-col">
      {hasDiscount && (
        <span className="text-[10px] text-gray-400 line-through">{formatCurrency(originalPrice)}</span>
      )}
      <span className={`text-xs font-medium ${hasDiscount ? 'text-green-700' : 'text-gray-700'}`}>
        {currentPrice ? formatCurrency(currentPrice) : (originalPrice ? formatCurrency(originalPrice) : '-')}
      </span>
      {hasDiscount && discountPercent && (
        <span className="text-[9px] font-semibold text-green-600">-{discountPercent}%</span>
      )}
    </div>
  );
}

// Partial porque estes resumos tambem recebem ListingVariation, que carrega os
// mesmos campos como opcionais. Todos os consumidores ja usam `|| 0`, entao o
// contrato real sempre foi "campo pode faltar".
type AdsMetrics = Partial<Pick<SellerListing, 'revenue' | 'paidCost' | 'tacos' | 'adSpend' | 'adRevenue' | 'adOrders' | 'adUnits' | 'adClicks' | 'adImpressions' | 'adCtr' | 'adCpc' | 'adRoas' | 'adAcos' | 'affiliateSales' | 'affiliateCommission' | 'affiliateItemsSold' | 'affiliateCount' | 'affiliateAcos'>>;

function AdsSummary({ metrics, compact = false }: { metrics: AdsMetrics; compact?: boolean }) {
  const hasAds = (metrics.adSpend || 0) > 0 || (metrics.adClicks || 0) > 0 || (metrics.adRevenue || 0) > 0;
  if (!hasAds) return null;
  const items = [
    { label: 'Gasto', value: formatCurrency(metrics.adSpend || 0), strong: true },
    { label: 'GMV atrib.', value: formatCurrency(metrics.adRevenue || 0), strong: false },
    { label: 'ROAS', value: (metrics.adSpend || 0) > 0 ? `${(metrics.adRoas || 0).toFixed(2)}x` : '-', strong: false },
    { label: 'ACOS', value: (metrics.adAcos || 0) > 0 ? formatPercent(metrics.adAcos || 0) : '-', strong: false },
    { label: 'Cliques', value: (metrics.adClicks || 0).toLocaleString('pt-BR'), strong: false },
    { label: 'CPC', value: (metrics.adCpc || 0) > 0 ? formatCurrency(metrics.adCpc || 0) : '-', strong: false },
  ];
  return (
    <div className={`mt-1.5 flex max-w-[540px] flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-emerald-100 bg-emerald-50/70 px-2 py-1 ${compact ? 'text-[9px]' : 'text-[10px]'}`} title="GMV atribuído pela Shopee/ML ao clique do anúncio. Na Shopee, direct_gmv usa janela de atribuição pós-clique, normalmente 7 dias, então não é a mesma base da receita total do anúncio no período.">
      <span className="shrink-0 font-semibold text-emerald-700">Ads atrib.</span>
      {items.map((item) => (
        <span key={item.label} className="whitespace-nowrap text-emerald-900/80">
          <span className="text-emerald-700/70">{item.label}</span>{' '}
          <span className={item.strong ? 'font-semibold text-emerald-800' : 'font-medium'}>{item.value}</span>
        </span>
      ))}
    </div>
  );
}

function AffiliateSummary({ metrics, compact = false }: { metrics: AdsMetrics; compact?: boolean }) {
  const hasAffiliate = (metrics.affiliateSales || 0) > 0 || (metrics.affiliateCommission || 0) > 0 || (metrics.affiliateItemsSold || 0) > 0;
  if (!hasAffiliate) return null;
  const items = [
    { label: 'Venda', value: formatCurrency(metrics.affiliateSales || 0), strong: true },
    { label: 'Comissão', value: formatCurrency(metrics.affiliateCommission || 0), strong: false },
    { label: 'ACOS', value: (metrics.affiliateAcos || 0) > 0 ? formatPercent(metrics.affiliateAcos || 0) : '-', strong: false },
    { label: 'Und.', value: (metrics.affiliateItemsSold || 0).toLocaleString('pt-BR'), strong: false },
    { label: 'Afiliados', value: (metrics.affiliateCount || 0).toLocaleString('pt-BR'), strong: false },
  ];
  return (
    <div className={`mt-1 flex max-w-[540px] flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-sky-100 bg-sky-50/75 px-2 py-1 ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
      <span className="shrink-0 font-semibold text-sky-700">Afiliados</span>
      {items.map((item) => (
        <span key={item.label} className="whitespace-nowrap text-sky-900/80">
          <span className="text-sky-700/70">{item.label}</span>{' '}
          <span className={item.strong ? 'font-semibold text-sky-800' : 'font-medium'}>{item.value}</span>
        </span>
      ))}
    </div>
  );
}


function TacosSummary({ metrics, compact = false }: { metrics: AdsMetrics; compact?: boolean }) {
  const paidCost = metrics.paidCost ?? ((metrics.adSpend || 0) + (metrics.affiliateCommission || 0));
  const tacos = metrics.tacos ?? ((metrics.revenue || 0) > 0 ? paidCost / (metrics.revenue || 0) : 0);
  if (paidCost <= 0 && tacos <= 0) return null;
  return (
    <div className={`mt-1 flex max-w-[540px] flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-fuchsia-100 bg-fuchsia-50/75 px-2 py-1 ${compact ? 'text-[9px]' : 'text-[10px]'}`} title="TACOS = (gasto em Ads + comissão de afiliados) dividido pela receita total do anúncio no período.">
      <span className="shrink-0 font-semibold text-fuchsia-700">TACOS</span>
      <span className="whitespace-nowrap text-fuchsia-900/80"><span className="text-fuchsia-700/70">Custo</span> <span className="font-medium">{formatCurrency(paidCost)}</span></span>
      <span className="whitespace-nowrap text-fuchsia-900/80"><span className="text-fuchsia-700/70">Receita</span> <span className="font-medium">{formatCurrency(metrics.revenue || 0)}</span></span>
      <span className="whitespace-nowrap text-fuchsia-900/80"><span className="text-fuchsia-700/70">TACOS</span> <span className="font-semibold text-fuchsia-800">{tacos > 0 ? formatPercent(tacos) : '-'}</span></span>
    </div>
  );
}
function PromotionsModal({ listing, onClose }: { listing: SellerListing; onClose: () => void }) {
  const promotions = listing.promotions || [];
  const typeLabels: Record<string, string> = {
    discount: 'Desconto',
    flash_sale: 'Oferta Relâmpago',
    bundle_deal: 'Compre Junto',
    deal: 'Oferta',
    seller_campaign: 'Campanha do Vendedor',
    volume_campaign: 'Desconto por Volume',
    marketplace_campaign: 'Campanha do Marketplace',
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-900">Promoções ativas</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm">x</button>
        </div>
        <p className="text-xs text-gray-500 mb-3 truncate">{listing.title}</p>
        {promotions.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Nenhuma promoção vinculada.</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {promotions.map((promo, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
                <div>
                  <p className="text-xs font-semibold text-gray-800">{promo.name || typeLabels[promo.type] || promo.type}</p>
                  <p className="text-[10px] text-gray-500">{typeLabels[promo.type] || promo.type}</p>
                </div>
                <div className="text-right">
                  {promo.promoPrice ? (
                    <span className="text-xs font-bold text-green-700">{formatCurrency(promo.promoPrice)}</span>
                  ) : (
                    <span className="text-[10px] text-gray-400">-</span>
                  )}
                  <span className={`ml-2 inline-block rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${promo.status === 'ongoing' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {promo.status === 'ongoing' ? 'Ativa' : 'Agendada'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

type ListingsMode = 'tabela' | 'quadro';

// Tela "Anuncios": metricas (Tabela) e saude (Quadro) do mesmo anuncio, com o
// diagnostico da IA num painel lateral e o impulsionamento num painel proprio.
export function SellerListingsView({ canTable = true, canBoard = true, canBoost = true }: { canTable?: boolean; canBoard?: boolean; canBoost?: boolean }) {
  const [params, setParams] = useSearchParams();
  const requestedMode = params.get('view') === 'quadro' ? 'quadro' : 'tabela';
  const mode: ListingsMode = !canTable ? 'quadro' : !canBoard ? 'tabela' : requestedMode;
  const setMode = (next: ListingsMode) => {
    const p = new URLSearchParams(params);
    if (next === 'tabela') p.delete('view'); else p.set('view', next);
    setParams(p, { replace: true });
  };
  const boostOpen = canBoost && params.get('impulsionar') === '1';
  const setBoostOpen = (open: boolean) => {
    const p = new URLSearchParams(params);
    if (open) p.set('impulsionar', '1'); else p.delete('impulsionar');
    setParams(p, { replace: true });
  };
  const [diagnosis, setDiagnosis] = useState<{ id: string; title?: string | null } | null>(null);
  const [grouping, setGrouping] = useBoardGrouping('seller_listings_board_grouping');
  const [connectionId, setConnectionId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [sort, setSort] = useState('revenue');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [periodDays, setPeriodDays] = useState(7);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [promoModal, setPromoModal] = useState<SellerListing | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const { connections } = useMarketplaceConnections();
  // O marketplace é derivado da conexão selecionada (cada conexão é de um marketplace).
  const selectedConn = connections.find((c) => c.connectionId === connectionId);
  const marketplace = selectedConn?.marketplace || '';

  const { listings, total, totalPages, loading, error, syncing, syncingAds, syncingAffiliates, syncJob, syncAll, deleteListing } = useSellerListings({
    marketplace: marketplace || undefined,
    connectionId: connectionId || undefined,
    search: searchDebounced || undefined,
    sort,
    order,
    periodDays,
    page,
    pageSize,
  });

  const {
    columns: healthColumns, progress: analysisProgress, running: analyzing, runAnalysis,
    fetchHistory, reviewAnalysis, deleteHistory, deleteAllHistory,
  } = useSellerAnalysis({ marketplace: marketplace || undefined, connectionId: connectionId || undefined });
  const { isEnabled: isAnalysisEnabled, setEnabled: setAnalysisEnabled } = useAnalysisPrefs();
  // Contador do botao carrega uma vez; so atualiza a cada 30 s com o painel aberto.
  const boost = useShopeeBoost(canBoost, boostOpen);

  // Uma analise por anuncio (a mais recente), para a coluna Saude e o quadro.
  const healthCards = useMemo(() => {
    const byListing = new Map<string, HealthCard>();
    for (const card of healthColumns ? Object.values(healthColumns).flat() : []) {
      const prev = byListing.get(card.listingIdExt);
      if (!prev || new Date(card.analyzedAt) > new Date(prev.analyzedAt)) byListing.set(card.listingIdExt, card);
    }
    return byListing;
  }, [healthColumns]);
  const boardCards = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = Array.from(healthCards.values());
    return q ? all.filter((c) => (c.title || '').toLowerCase().includes(q) || c.listingIdExt.includes(q) || (c.sku || '').toLowerCase().includes(q)) : all;
  }, [healthCards, search]);
  const toReview = useMemo(() => Array.from(healthCards.values()).filter((c) => listingFlowStage(c) === 'revisar').length, [healthCards]);

  const moveCard = async (card: HealthCard, reviewStatus: string) => {
    const res = await reviewAnalysis(card.id, { reviewStatus });
    if (!res?.success) toast.error(res?.message || 'Falha ao mover o anúncio');
  };

  const handleDeleteAllHistory = async () => {
    if (!confirm('Excluir o histórico de análises de TODOS os anúncios? Essa ação não pode ser desfeita.')) return;
    const res = await deleteAllHistory();
    if (res?.success) toast.success(`Histórico excluído (${res.deleted ?? 0} registro(s))`);
    else toast.error(res?.message || 'Falha ao excluir');
  };

  const bulkSetAnalysis = async (enabled: boolean) => {
    if (selectedIds.size === 0) return;
    await setAnalysisEnabled(Array.from(selectedIds), enabled);
    toast.success(`Analise ${enabled ? 'ativada' : 'desativada'} para ${selectedIds.size} anuncio(s)`);
  };

  const handleAnalyze = async () => {
    const res = await runAnalysis({ periodDays, marketplace: marketplace || undefined, connectionId: connectionId || undefined });
    if (res?.success) {
      toast.success('Análise iniciada. Os anúncios aparecem no quadro conforme ficam prontos.');
      if (canBoard) setMode('quadro');
    } else {
      toast.error(res?.message || 'Falha ao iniciar analise');
    }
  };

  const handleSort = (field: string) => {
    if (sort === field) {
      setOrder(order === 'desc' ? 'asc' : 'desc');
    } else {
      setSort(field);
      setOrder('desc');
    }
    setPage(1);
  };

  const handleSearch = (val: string) => {
    setSearch(val);
    clearTimeout((window as any).__sellerSearchTimer);
    (window as any).__sellerSearchTimer = setTimeout(() => {
      setSearchDebounced(val);
      setPage(1);
    }, 400);
  };

  const handleSync = async () => {
    await syncAll(connectionId || undefined, [7, 30]);
  };

  const syncActive = Boolean(syncJob && ['queued', 'running'].includes(syncJob.status));
  const syncFailedCount = syncJob?.errors?.length || 0;
  const syncCompletedAt = formatDateTime(syncJob?.completedAt);
  const toggleSelect = (listingIdExt: string) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(listingIdExt)) s.delete(listingIdExt); else s.add(listingIdExt);
      return s;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === listings.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(listings.map(l => l.listingIdExt)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Apagar métricas de ${selectedIds.size} anúncio(s)?\n\nEssa ação não pode ser desfeita.`)) return;
    const count = selectedIds.size;
    setBulkDeleting(true);
    for (const id of selectedIds) {
      await deleteListing(id);
    }
    setSelectedIds(new Set());
    setBulkDeleting(false);
    toast.success(`Métricas de ${count} anúncio(s) apagadas`);
  };

  const syncRunning = syncActive || syncing || syncingAds || syncingAffiliates;
  const moreActions: OverflowItem[] = canBoard
    ? [{ label: 'Excluir histórico de análises (todos)', hint: 'Apaga os diagnósticos de todos os anúncios', danger: true, disabled: analyzing || healthCards.size === 0, onClick: handleDeleteAllHistory }]
    : [];
  const syncResult = syncJob ? (
    <>
      <div>
        {syncJob.completedSteps || 0} de {syncJob.totalSteps || 0} etapas
        {syncFailedCount > 0 ? ` - ${syncFailedCount} falha(s) parcial(is)` : ''}
        {syncCompletedAt ? ` - concluida em ${syncCompletedAt}` : ''}
      </div>
      {syncFailedCount > 0 && (
        <div className="mt-1 max-h-24 overflow-y-auto text-amber-700">
          {syncJob.errors?.slice(-3).map((err, idx) => (
            <div key={`${err.step}-${idx}`} className="truncate">{err.step}: {err.message}</div>
          ))}
        </div>
      )}
    </>
  ) : null;

  return (
    <div className="space-y-5">
      <SellerAutomationHeader
        title="Anúncios"
        subtitle="Desempenho e saúde de cada anúncio. Clique num anúncio para ver o diagnóstico da IA."
        automationKeys={[...(canTable ? ['listings-sync'] : []), ...(canBoard ? ['analysis'] : [])]}
        actions={
          <>
            {canBoost && (
              <SellerRunButton
                label={`⚡ Impulsionar ${boost.data ? `${boost.data.boostedCount}/5` : ''}`.trim()}
                title="Vagas de destaque da Shopee"
                onClick={() => setBoostOpen(true)}
              />
            )}
            {canTable && (
              <SellerRunButton
                label="Sincronizar"
                runningLabel="Sincronizando..."
                running={syncRunning}
                disabled={connections.length === 0}
                onClick={handleSync}
              />
            )}
            {canBoard && (
              <CreditConfirmRunButton
                featureKey="listing_analysis"
                unitLabel="anúncio(s)"
                estimate={async () => (await api.estimateSellerAnalysis({ marketplace: marketplace || undefined, connectionId: connectionId || undefined })).count}
                onRun={handleAnalyze}
                running={analyzing}
                label="Analisar com IA"
                runningLabel="Analisando..."
                title="Analisar anúncios com IA"
                note="Cobrado por anúncio com IA ativada. Anúncios em cooldown ou sem dados não são cobrados."
                className={sellerRunButtonClass('primary')}
              />
            )}
          </>
        }
        more={moreActions}
        progress={
          analyzing && analysisProgress
            ? { percent: analysisProgress.total > 0 ? (analysisProgress.done / analysisProgress.total) * 100 : null, label: `Analisando anúncios... (${analysisProgress.analyzed} analisados, ${analysisProgress.skipped} em cooldown)` }
            : syncJob ? { percent: syncJob.progressPercent || 0, label: syncJob.currentStep || 'Sincronizando métricas' } : null
        }
        result={syncResult}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        {canTable && canBoard ? (
          <SegmentedToggle<ListingsMode>
            value={mode}
            onChange={setMode}
            options={[{ value: 'tabela', label: 'Tabela' }, { value: 'quadro', label: 'Quadro', count: toReview || undefined }]}
          />
        ) : <span />}
        {mode === 'quadro' && <BoardGroupingToggle value={grouping} onChange={setGrouping} />}
        {mode === 'quadro' && connections.length > 0 && (
          <select
            value={connectionId}
            onChange={(e) => { setConnectionId(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700"
          >
            <option value="">Todas conexões</option>
            {connections.map((c) => (
              <option key={c.connectionId} value={c.connectionId}>{c.connectionName || c.connectionId.slice(0, 8)}</option>
            ))}
          </select>
        )}
      </div>

      {mode === 'quadro' && (
        <>
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Filtrar por título, SKU ou ID..."
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-orange-300 focus:outline-none focus:ring-1 focus:ring-orange-300"
          />
          {healthCards.size === 0 ? (
            <EmptyState title="Nenhum anúncio analisado ainda">Clique em "Analisar com IA" para gerar os diagnósticos. Em "Agrupar por etapa" dá para arrastar os cards conforme você trabalha.</EmptyState>
          ) : (
            <ListingsBoard cards={boardCards} grouping={grouping} onOpen={(card) => setDiagnosis({ id: card.listingIdExt, title: card.title })} onMove={moveCard} />
          )}
        </>
      )}

      {mode === 'tabela' && <>
      {/* Filtros e ações em massa */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {selectedIds.size > 0 && (
            <>
              <button
                onClick={() => bulkSetAnalysis(true)}
                className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
              >
                Análise ON ({selectedIds.size})
              </button>
              <button
                onClick={() => bulkSetAnalysis(false)}
                className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-100"
              >
                Análise OFF ({selectedIds.size})
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="rounded-lg bg-red-50 border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 transition disabled:opacity-50"
              >
                {bulkDeleting ? 'Apagando...' : `Apagar ${selectedIds.size} selecionado${selectedIds.size > 1 ? 's' : ''}`}
              </button>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Period toggle */}
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
            {[7, 30].map((d) => (
              <button
                key={d}
                onClick={() => { setPeriodDays(d); setPage(1); }}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                  periodDays === d ? 'bg-orange-700 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>

          {/* Page size */}
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            title="Anuncios por pagina"
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700"
          >
            {[20, 50, 100, 200, 500, 1000].map((n) => (
              <option key={n} value={n}>{n}/pág</option>
            ))}
          </select>

          {/* Connection filter */}
          {connections.length > 0 && (
            <select
              value={connectionId}
              onChange={(e) => { setConnectionId(e.target.value); setPage(1); }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700"
            >
              <option value="">Todas conexões</option>
              {connections.map((c) => (
                <option key={c.connectionId} value={c.connectionId}>
                  {c.connectionName || c.connectionId.slice(0, 8)}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        <input
          type="text"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Buscar por titulo ou ID..."
          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-4 text-sm text-gray-700 placeholder-gray-400 focus:border-orange-300 focus:outline-none focus:ring-1 focus:ring-orange-300"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {/* Empty state */}
      {!loading && listings.length === 0 && !error && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50/50 py-16 text-center">
          <svg className="mb-3 h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1}><path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" /></svg>
          <p className="text-sm font-medium text-gray-600">Nenhum anúncio vinculado</p>
          <p className="mt-1 text-xs text-gray-400">Vincule anúncios aos seus produtos na aba Produtos. Depois clique em "Sincronizar" para carregar as métricas.</p>
        </div>
      )}

      {/* Table */}
      {(loading || listings.length > 0) && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full min-w-[1000px] border-collapse text-sm [&_td]:border-r [&_td]:border-gray-100 [&_td:last-child]:border-r-0 [&_th]:border-r [&_th]:border-gray-100 [&_th:last-child]:border-r-0 [&_tr]:border-b [&_tr]:border-gray-100">
            <thead className="border-b border-gray-100 bg-gray-50/80">
              <tr>
                <th className="px-2 py-2.5 text-left">
                  <input
                    type="checkbox"
                    checked={listings.length > 0 && selectedIds.size === listings.length}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500">Produto</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500">Preço</th>
                <th className="px-3 py-2.5 text-left"><SortButton label="Impressões" field="impressions" current={sort} order={order} onSort={handleSort} /></th>
                <th className="px-3 py-2.5 text-left"><SortButton label="Cliques" field="clicks" current={sort} order={order} onSort={handleSort} /></th>
                <th className="px-3 py-2.5 text-left"><SortButton label="Visitas" field="visitors" current={sort} order={order} onSort={handleSort} /></th>
                <th className="px-3 py-2.5 text-left"><SortButton label="Pedidos" field="orders" current={sort} order={order} onSort={handleSort} /></th>
                <th className="px-3 py-2.5 text-left"><SortButton label="Und. Vend." field="units" current={sort} order={order} onSort={handleSort} /></th>
                <th className="px-3 py-2.5 text-left"><SortButton label="Receita" field="revenue" current={sort} order={order} onSort={handleSort} /></th>
                <th className="px-3 py-2.5 text-left"><SortButton label="Conv." field="conversion" current={sort} order={order} onSort={handleSort} /></th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500">Avaliação</th>
                {canBoard && <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500">Saúde</th>}
                <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500" title="Incluir este anúncio quando rodar a análise com IA">IA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading && listings.length === 0 ? (
                <tr><td colSpan={13} className="py-12 text-center text-gray-400">Carregando...</td></tr>
              ) : (
                listings.map((listing: SellerListing) => (
                  <ListingRow key={listing.id} listing={listing} health={canBoard ? { card: healthCards.get(listing.listingIdExt), open: () => setDiagnosis({ id: listing.listingIdExt, title: listing.title }) } : undefined} onShowPromos={setPromoModal} onDelete={deleteListing} selected={selectedIds.has(listing.listingIdExt)} onToggleSelect={toggleSelect} analysisEnabled={isAnalysisEnabled(listing.listingIdExt)} onToggleAnalysis={(en) => setAnalysisEnabled(listing.listingIdExt, en)} varAnalysisEnabled={isAnalysisEnabled} onToggleVarAnalysis={(id, en) => setAnalysisEnabled(id, en)} />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{total} anúncio{total !== 1 ? 's' : ''}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="rounded border px-2 py-1 disabled:opacity-40">←</button>
            <span className="px-2">{page} / {totalPages}</span>
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="rounded border px-2 py-1 disabled:opacity-40">→</button>
          </div>
        </div>
      )}

      </>}

      {/* Promotions modal */}
      {promoModal && <PromotionsModal listing={promoModal} onClose={() => setPromoModal(null)} />}

      {diagnosis && (
        <ListingDiagnosisDrawer
          listingIdExt={diagnosis.id}
          fallbackTitle={diagnosis.title}
          onClose={() => setDiagnosis(null)}
          fetchHistory={fetchHistory}
          reviewAnalysis={reviewAnalysis}
          deleteHistory={canBoard ? deleteHistory : undefined}
          analysisEnabled={isAnalysisEnabled(diagnosis.id)}
          onToggleAnalysis={(en) => setAnalysisEnabled(diagnosis.id, en)}
        />
      )}

      {boostOpen && <SellerBoostDrawer state={boost} onClose={() => setBoostOpen(false)} />}
    </div>
  );
}

function ListingRow({ listing, health, onShowPromos, onDelete, selected, onToggleSelect, analysisEnabled = true, onToggleAnalysis, varAnalysisEnabled, onToggleVarAnalysis }: { listing: SellerListing; health?: { card?: HealthCard; open: () => void }; onShowPromos: (l: SellerListing) => void; onDelete: (id: string) => Promise<boolean>; selected?: boolean; onToggleSelect?: (id: string) => void; analysisEnabled?: boolean; onToggleAnalysis?: (enabled: boolean) => void; varAnalysisEnabled?: (id: string) => boolean; onToggleVarAnalysis?: (id: string, enabled: boolean) => void }) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const hasVariations = listing.hasVariations && listing.variations && listing.variations.length > 0;
  const channelId = listing.listingIdExt || '-';

  const handleDelete = async () => {
    if (!confirm(`Apagar todos os registros de métricas do anúncio "${listing.title || listing.listingIdExt}"? Essa ação não pode ser desfeita.`)) return;
    setDeleting(true);
    const ok = await onDelete(listing.listingIdExt);
    setDeleting(false);
    if (ok) toast.success('Métricas do anúncio apagadas');
  };

  return (
    <>
      {/* Parent row */}
      <tr className="hover:bg-gray-50/50 transition">
        {/* Checkbox */}
        <td className="px-2 py-2.5">
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect?.(listing.listingIdExt)}
            className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
          />
        </td>
        {/* Product info */}
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2.5">
            {hasVariations ? (
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
              >
                <span className={`text-xs transition-transform ${expanded ? 'rotate-90' : ''}`}>›</span>
              </button>
            ) : (
              <span className="w-5" />
            )}
            {listing.imageUrl ? (
              <img src={listing.imageUrl} alt="" className="h-10 w-10 rounded-lg border border-gray-100 object-cover" />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-400">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5" /></svg>
              </div>
            )}
            <div className="min-w-0 flex-1">
              {health ? (
                <button type="button" onClick={health.open} className="max-w-[360px] whitespace-normal break-words text-left text-[13px] font-medium leading-snug text-gray-900 hover:text-orange-700 hover:underline">{listing.title || 'Sem título'}</button>
              ) : (
                <p className="text-[13px] leading-snug font-medium text-gray-900 max-w-[360px] whitespace-normal break-words">{listing.title || 'Sem título'}</p>
              )}
              <div className="flex items-center gap-1.5 mt-0.5">
                <MarketplaceBadge marketplace={listing.marketplace} />
                {hasVariations && (
                  <span className="text-[10px] text-gray-400 bg-gray-100 rounded px-1">{listing.variations!.length} variações</span>
                )}
                <span className="text-[10px] text-slate-500 bg-slate-100 rounded px-1">{listing.photoCount || 0} fotos</span>
                {(listing.videoCount || 0) > 0 && <span className="text-[10px] text-indigo-600 bg-indigo-50 rounded px-1">{listing.videoCount} vídeo{listing.videoCount === 1 ? '' : 's'}</span>}
              </div>
              {listing.product && (
                <p className="mt-0.5 text-[10px] text-gray-500">
                  SKU: {listing.product.sku || '-'} - ID Canal: {channelId}
                </p>
              )}
              <AdsSummary metrics={listing} />
              <AffiliateSummary metrics={listing} />
              <TacosSummary metrics={listing} />
            </div>
          </div>
        </td>
        {/* Price */}
        <td className="px-3 py-2.5">
          <PriceDisplay originalPrice={listing.originalPrice} currentPrice={listing.currentPrice} discountPercent={listing.discountPercent} />
          {listing.promotions && listing.promotions.length > 0 && (
            <div className="mt-1">
              <PromoBadge promotions={listing.promotions} onClick={() => onShowPromos(listing)} />
            </div>
          )}
        </td>
        <td className="px-3 py-2.5 text-gray-700">
          {listing.marketplace === 'mercadolivre'
            ? <span className="text-xs text-gray-400" title="O Mercado Livre não fornece esta métrica">n/d</span>
            : listing.impressions.toLocaleString('pt-BR')}
        </td>
        <td className="px-3 py-2.5">
          {listing.marketplace === 'mercadolivre' ? (
            <span className="text-xs text-gray-400" title="O Mercado Livre não fornece esta métrica">n/d</span>
          ) : (
            <>
              <span className="text-gray-700">{listing.clicks.toLocaleString('pt-BR')}</span>
              <span className="ml-1 text-[10px] text-gray-400">{formatPercent(listing.ctr)}</span>
              <DeltaBadge delta={listing.comparison?.clicks?.delta} />
            </>
          )}
        </td>
        <td className="px-3 py-2.5 text-gray-700">
          {listing.visitors.toLocaleString('pt-BR')}
          <DeltaBadge delta={listing.comparison?.visitors?.delta} />
        </td>
        <td className="px-3 py-2.5 text-gray-700">
          {listing.orders.toLocaleString('pt-BR')}
          <DeltaBadge delta={listing.comparison?.orders?.delta} />
        </td>
        <td className="px-3 py-2.5 text-gray-700">
          {listing.units.toLocaleString('pt-BR')}
        </td>
        <td className="px-3 py-2.5 font-medium text-gray-900">
          {formatCurrency(listing.revenue)}
          <DeltaBadge delta={listing.comparison?.revenue?.delta} />
        </td>
        <td className="px-3 py-2.5">
          <span className={`text-xs font-medium ${listing.conversion > 0.03 ? 'text-green-600' : listing.conversion > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
            {formatPercent(listing.conversion)}
          </span>
        </td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            {listing.ratingAverage != null ? (
              <span className="inline-flex items-center gap-1 text-xs text-gray-700">
                <span className="text-yellow-500">★</span>
                <span className="font-medium">{listing.ratingAverage.toFixed(1)}</span>
                <span className="text-[10px] text-gray-400">({(listing.reviewsCount ?? 0).toLocaleString('pt-BR')})</span>
              </span>
            ) : (
              <span className="text-xs text-gray-300">-</span>
            )}
            <button
              onClick={handleDelete}
              disabled={deleting}
              title="Apagar métricas deste anúncio"
              className="ml-auto rounded p-1 text-gray-300 hover:bg-red-50 hover:text-red-500 transition disabled:opacity-40"
            >
              {deleting ? (
                <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              ) : (
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
              )}
            </button>
          </div>
        </td>
        {health && (
          <td className="px-3 py-2.5">
            <HealthBadge card={health.card} onClick={health.open} />
          </td>
        )}
        <td className="px-3 py-2.5 text-center">
          <button
            onClick={() => onToggleAnalysis?.(!analysisEnabled)}
            title={analysisEnabled ? 'Analise ligada - clique para desligar' : 'Analise desligada - clique para ligar'}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold transition ${
              analysisEnabled ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${analysisEnabled ? 'bg-emerald-500' : 'bg-gray-400'}`} />
            {analysisEnabled ? 'ON' : 'OFF'}
          </button>
        </td>
      </tr>

      {/* Variation rows (children) */}
      {expanded && hasVariations && listing.variations!.map((v) => {
        const variationChannelId = v.listingIdExt || v.variationId || v.modelId || '-';
        return (
        <tr key={v.id} className="bg-gray-50/40">
          <td className="px-2 py-2"></td>
          <td className="px-3 py-2 pl-14">
            <div className="flex items-center gap-2">
              <span className="text-gray-300 text-xs">└</span>
              {v.imageUrl ? (
                <img src={v.imageUrl} alt="" className="h-7 w-7 rounded border border-gray-100 object-cover" />
              ) : (
                <div className="h-7 w-7 rounded bg-gray-100" />
              )}
              <div className="min-w-0">
                <p className="truncate text-xs font-medium text-gray-700 max-w-[180px]">{v.name}</p>
                <p className="text-[10px] text-gray-400">SKU: {v.sku || '-'} - ID Canal: {variationChannelId}</p>
                <div className="mt-0.5 flex items-center gap-1">
                  <span className="text-[10px] text-slate-500 bg-slate-100 rounded px-1">{v.photoCount || 0} fotos</span>
                  {(v.videoCount || 0) > 0 && <span className="text-[10px] text-indigo-600 bg-indigo-50 rounded px-1">{v.videoCount} vídeo{v.videoCount === 1 ? '' : 's'}</span>}
                </div>
                <AdsSummary metrics={v} compact />
                <AffiliateSummary metrics={v} compact />
                <TacosSummary metrics={v} compact />
              </div>
            </div>
          </td>
          <td className="px-3 py-2">
            {v.currentPrice || v.originalPrice ? (
              <PriceDisplay originalPrice={v.originalPrice} currentPrice={v.currentPrice} discountPercent={v.discountPercent} />
            ) : v.price ? (
              <span className="text-xs text-gray-700">{formatCurrency(v.price)}</span>
            ) : (
              <span className="text-xs text-gray-300">-</span>
            )}
          </td>
          {listing.hasVariationMetrics ? (
            <>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-600">{(v.visitors || 0).toLocaleString('pt-BR')}</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-600">{(v.orders || 0).toLocaleString('pt-BR')}</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-600">{(v.units || 0).toLocaleString('pt-BR')}</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-600">{formatCurrency(v.revenue || 0)}</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-600">{((v.conversion || 0) * 100).toFixed(1)}%</span></td>
            </>
          ) : listing.hasVariationSales ? (
            <>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-600">{(v.units || 0).toLocaleString('pt-BR')}</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-600">{v.revenue != null ? formatCurrency(v.revenue) : '-'}</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
            </>
          ) : (
            <>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
              <td className="px-3 py-2"><span className="text-xs text-gray-200">-</span></td>
            </>
          )}
          <td className="px-3 py-2">
            {v.ratingAverage != null ? (
              <span className="inline-flex items-center gap-1 text-xs text-gray-700">
                <span className="text-yellow-500">★</span>
                <span className="font-medium">{v.ratingAverage.toFixed(1)}</span>
                <span className="text-[10px] text-gray-400">({(v.reviewsCount ?? 0).toLocaleString('pt-BR')})</span>
              </span>
            ) : (
              <span className="text-xs text-gray-200">-</span>
            )}
          </td>
          {health && <td className="px-3 py-2"></td>}
          <td className="px-3 py-2 text-center">
            {listing.marketplace === 'mercadolivre' && listing.hasVariationMetrics && v.listingIdExt ? (
              (() => {
                const varEnabled = varAnalysisEnabled ? varAnalysisEnabled(v.listingIdExt) : true;
                const parentEnabled = analysisEnabled;
                const effective = varEnabled && parentEnabled;
                return (
                  <button
                    onClick={() => onToggleVarAnalysis?.(v.listingIdExt!, !varEnabled)}
                    disabled={!parentEnabled}
                    title={!parentEnabled ? 'Anuncio pai esta com analise OFF' : (varEnabled ? 'Analise da variacao ligada - clique para desligar' : 'Analise da variacao desligada - clique para ligar')}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold transition disabled:opacity-40 ${
                      effective ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${effective ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                    {effective ? 'ON' : 'OFF'}
                  </button>
                );
              })()
            ) : (
              <span className="text-xs text-gray-200">-</span>
            )}
          </td>
        </tr>
      );})}
    </>
  );
}
